import { useEffect, useRef, useState } from 'react';
import { Check, Link2, Mail, MessageCircle, Share2, XTwitter } from '@/components/icons';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSnackbar } from '@/components/ui/magic-snackbar';
import { cn } from '@/lib/utils';
import { canonical } from '../lib/site';
import { loadKakao, shareToKakao } from '../lib/kakao';

/**
 * 공유 — 글·아티클 상세의 좋아요/싫어요 옆 둥근 아이콘 버튼 하나. 누르면 **우리 메뉴**가 뜬다.
 * 항목은 어느 브라우저든 같다: 카카오톡 · X · 이메일 · 링크 복사.
 *
 * - 카카오톡: 카카오 SDK(느리게 로드). 키가 없거나 로드 실패면 링크 복사로 내려간다.
 * - X: intent URL 새 창. SDK 없음.
 * - Gmail: Gmail 작성 창을 새 탭으로. mailto 는 메일 앱 핸들러가 없는 브라우저에서 조용히 죽어서 뺐다.
 * - 링크 복사: 클립보드. 안 되면 읽기 전용 입력창 대화상자(아래 CopyLinkDialog).
 *
 * 공유된 링크의 미리보기(OG)는 SSR <head> 가 낸다.
 */
export function ShareActions({
  targetId,
  title,
  description,
  path,
}: {
  targetId: string;
  title: string;
  description: string;
  /** Canonical path to share; defaults to the community post detail. */
  path?: string;
}) {
  const { showSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const url = canonical(path ?? `/posts/${targetId}`);
  const kakaoEnabled = Boolean(import.meta.env.VITE_KAKAO_JS_KEY);

  // 바깥 클릭 · Escape 로 닫는다.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch {
      // Clipboard write rejected (e.g. permission / insecure context) → fall back.
    }
    // Graceful degradation: in-app manual-copy dialog instead of window.prompt.
    setFallbackOpen(true);
  };

  const shareKakao = async () => {
    if (kakaoEnabled && (await loadKakao())) {
      shareToKakao({ title, description, url });
      return;
    }
    // SDK 를 못 불렀다 — 메뉴는 이미 닫혔으니 스낵바로 알리고 링크를 복사한다.
    showSnackbar('카카오톡 공유를 열지 못했다. 링크를 복사했다.', { type: 'comment' });
    void copyLink();
  };

  const shareX = () => {
    const href = `https://x.com/intent/post?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const shareGmail = () => {
    const body = description && description !== title ? `${description}\n\n${url}` : url;
    const href = `https://mail.google.com/mail/?view=cm&su=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const items: ReadonlyArray<{ label: string; icon: React.ReactNode; run: () => void }> = [
    { label: '카카오톡', icon: <MessageCircle />, run: () => void shareKakao() },
    { label: 'X', icon: <XTwitter />, run: shareX },
    { label: 'Gmail', icon: <Mail />, run: shareGmail },
    { label: copied ? '링크 복사됨' : '링크 복사', icon: copied ? <Check /> : <Link2 />, run: () => void copyLink() },
  ];

  return (
    <div ref={rootRef} className="relative" aria-label="공유">
      <IconAction label="공유" onClick={() => setOpen((o) => !o)} active={open} expanded={open}>
        <Share2 />
      </IconAction>

      {open ? (
        <div
          role="menu"
          aria-label="공유 방법"
          className="absolute right-0 top-full z-50 mt-2 w-44 rounded-xl border border-border bg-white p-1 shadow-lg"
        >
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              onClick={() => { it.run(); if (!it.label.startsWith('링크')) setOpen(false); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-text hover:bg-gray-100 [&_svg]:size-4 [&_svg]:text-text-muted"
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>
      ) : null}

      <CopyLinkDialog
        open={fallbackOpen}
        url={url}
        onClose={() => setFallbackOpen(false)}
      />
    </div>
  );
}

/** Round icon button matching the 좋아요/싫어요 reaction pills. */
function IconAction({
  label,
  onClick,
  active,
  expanded,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  expanded?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={expanded}
      title={label}
      className={cn(
        'inline-flex size-11 items-center justify-center rounded-full border transition-colors [&_svg]:size-4',
        active
          ? 'border-primary bg-primary-soft text-primary'
          : 'border-border text-text-muted hover:bg-surface-muted',
      )}
    >
      {children}
    </button>
  );
}

/**
 * In-app fallback when the clipboard API is unavailable: a read-only input that is
 * auto-selected so the user can copy with one keystroke. Reuses the shared dialog
 * styles; Escape / overlay-click close it.
 */
function CopyLinkDialog({
  open,
  url,
  onClose,
}: {
  open: boolean;
  url: string;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.select();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const titleId = 'copy-link-dialog-title';

  return (
    <div
      className="fixed inset-0 z-[600] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-surface p-5 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="mb-2 text-base font-bold text-text">
          링크 복사
        </h2>
        <p className="mb-3 text-sm text-text-muted">
          아래 링크를 길게 눌러 복사하자.
        </p>
        <div className="mb-4">
          <label htmlFor="copy-link-input" className="sr-only">
            공유 링크
          </label>
          <Input
            id="copy-link-input"
            ref={inputRef}
            type="text"
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
        <div className="flex justify-end">
          <Button type="button" variant="primary" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
}
